import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '@byscott-io/corebyscott';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apps } from '../api/client';

export default function AppDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate(); // Used for delete redirect
  const [app, setApp] = useState(null);
  const [clientAccounts, setClientAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadApp = useCallback(async () => {
    try {
      setLoading(true);
      const [appResponse, clientAccountsResponse] = await Promise.all([
        apps.get(id),
        apps.clientAccounts(id),
      ]);
      setApp(appResponse.data.app);
      setClientAccounts(clientAccountsResponse.data.client_accounts);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load app');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadApp();
  }, [loadApp]);

  const handleRegenerateKey = async () => {
    if (!confirm('Are you sure you want to regenerate the API key? Existing integrations will stop working.')) {
      return;
    }
    try {
      const response = await apps.regenerateKey(id);
      setApp(response.data.app);
      setShowApiKey(true);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to regenerate key');
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await apps.delete(id);
      navigate('/app/apps');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete app');
      setDeleting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-gray-500">Loading app...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <Link to="/app/apps" className="text-blue-600 hover:text-blue-800">
          ← Back to Apps
        </Link>
      </div>

      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold">{app.name}</h1>
          <p className="text-gray-500">/{app.slug}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setEditing(true)}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Edit
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <span className="py-2 px-1 border-b-2 border-blue-500 text-blue-600 font-medium text-sm">
            Details
          </span>
          <Link
            to={`/app/apps/${id}/products`}
            className="py-2 px-1 border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 font-medium text-sm"
          >
            Products
          </Link>
          <Link
            to={`/app/apps/${id}/features`}
            className="py-2 px-1 border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 font-medium text-sm"
          >
            Features
          </Link>
          <Link
            to={`/app/apps/${id}/quota`}
            className="py-2 px-1 border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 font-medium text-sm"
          >
            Quota
          </Link>
          <Link
            to={`/app/apps/${id}/settings`}
            className="py-2 px-1 border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 font-medium text-sm"
          >
            Settings
          </Link>
          <Link
            to={`/app/apps/${id}/referral_codes`}
            className="py-2 px-1 border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 font-medium text-sm"
          >
            Referral Codes
          </Link>
          <Link
            to={`/app/apps/${id}/clients`}
            className="py-2 px-1 border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 font-medium text-sm"
          >
            Client Accounts ({clientAccounts.length})
          </Link>
        </nav>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* App Details */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Details</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500">Status</label>
              <div className="mt-1">
                {app.active ? (
                  <span className="px-2 py-1 text-sm rounded-full bg-green-100 text-green-800">
                    Active
                  </span>
                ) : (
                  <span className="px-2 py-1 text-sm rounded-full bg-gray-100 text-gray-800">
                    Inactive
                  </span>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Description</label>
              <p className="mt-1 text-gray-900">{app.description || '—'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Webhook URL</label>
              <p className="mt-1 text-gray-900 font-mono text-sm">{app.webhook_url || '—'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Created</label>
              <p className="mt-1 text-gray-900">
                {new Date(app.created_at).toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* API Key */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">API Key</h2>

          <p className="text-sm text-gray-500 mb-4">
            Use this API key to authenticate requests from your app to the billing API.
          </p>

          <div className="bg-gray-100 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <code className="text-sm font-mono break-all">
                {showApiKey ? app.api_key : '••••••••••••••••••••••••••••••••'}
              </code>
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  {showApiKey ? 'Hide' : 'Show'}
                </button>
                {showApiKey && (
                  <button
                    onClick={() => copyToClipboard(app.api_key)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Copy
                  </button>
                )}
              </div>
            </div>
          </div>

          <button
            onClick={handleRegenerateKey}
            className="text-red-600 hover:text-red-800 text-sm"
          >
            Regenerate API Key
          </button>
        </div>

        {/* Quick Stats */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Quick Stats</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-2xl font-bold text-blue-600">{clientAccounts.length}</p>
              <p className="text-sm text-gray-500">Client Accounts</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">0</p>
              <p className="text-sm text-gray-500">Active Subscriptions</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-600">0</p>
              <p className="text-sm text-gray-500">Plans</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-600">0</p>
              <p className="text-sm text-gray-500">Features</p>
            </div>
          </div>
        </div>

        {/* Integration Guide */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Integration Guide</h2>
          <p className="text-sm text-gray-500 mb-4">
            Add the billingbyscott_client to your app:
          </p>

          <div className="bg-gray-900 text-gray-100 rounded-lg p-4 font-mono text-sm mb-4">
            <p className="text-gray-400"># Gemfile</p>
            <p>gem &quot;billingbyscott_client&quot;</p>
          </div>

          <div className="bg-gray-900 text-gray-100 rounded-lg p-4 font-mono text-sm">
            <p className="text-gray-400"># config/initializers/billingbyscott.rb</p>
            <p>BillingbyscottClient.configure do |c|</p>
            <p>  c.api_key = &quot;{showApiKey ? app.api_key : 'YOUR_API_KEY'}&quot;</p>
            <p>end</p>
          </div>
        </div>
      </div>

      {editing && (
        <EditAppModal
          app={app}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            loadApp();
          }}
        />
      )}

      <Modal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete App"
        size="md"
        footer={
          <>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              disabled={deleting}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </>
        }
      >
        <p className="text-gray-600">
          Are you sure you want to delete <strong>{app.name}</strong>? This action cannot be undone.
        </p>
      </Modal>

    </div>
  );
}

function EditAppModal({ app, onClose, onSaved }) {
  const [name, setName] = useState(app.name);
  const [description, setDescription] = useState(app.description || '');
  const [webhookUrl, setWebhookUrl] = useState(app.webhook_url || '');
  const [active, setActive] = useState(app.active);
  const [allowMixedLicenses, setAllowMixedLicenses] = useState(app.allow_mixed_licenses !== false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      await apps.update(app.id, { name, description, webhook_url: webhookUrl, active, allow_mixed_licenses: allowMixedLicenses });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.errors?.join(', ') || 'Failed to update app');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Edit App" size="md">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              App Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Webhook URL
            </label>
            <input
              type="url"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://your-app.com/webhooks/billing"
            />
          </div>

          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Active</span>
            </label>
          </div>

          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={allowMixedLicenses}
                onChange={(e) => setAllowMixedLicenses(e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="ml-2 text-sm text-gray-700">Allow mixed licenses</span>
            </label>
            <p className="mt-1 text-xs text-gray-500 ml-6">
              {allowMixedLicenses
                ? 'Users can be assigned to different plans (e.g., 5 Pro + 3 Basic)'
                : 'All users share the same plan. Simpler billing model.'}
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !name}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
    </Modal>
  );
}

